import styled from "@emotion/styled"
import React, { useState, useEffect } from "react"
import moment, { Moment } from "moment"
import "moment/locale/cs"
import { Button, Popover } from "antd"
import { usePathname } from "next/navigation"
import { theme } from "../../styles/styles"

moment.locale("cs")

interface CourseDates {
  lessonDates: Moment[]
  lastLessonDate: Moment
  skippedDates: Moment[]
}

interface CalendarCourse {
  id: string
  name: string
  color: string
  registrationUrl: string
  courseDates: CourseDates
  firstCourseDay?: Moment
}

interface MultiScheduleCalendarProps {
  courses: CalendarCourse[]
}

const CalendarWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  width: 100%;
  font-size: 12px;

  .week-days {
    text-align: center;
  }
`

const DayCell = styled.div<{ isToday?: boolean; background?: string }>`
  padding: 6px 0;
  text-align: center;
  border-radius: 4px;
  border: ${({ isToday }) => (isToday ? "2px solid #01497c" : "1px solid #d9d9d9")};
  background-color: ${({ background }) => background || "#fff"};
  color: ${({ background }) => (background ? "#fff" : "inherit")};
  cursor: ${({ background }) => (background ? "pointer" : "default")};
`

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  align-items: center;
`

const daysOfWeek = moment.weekdaysShort(true)

const MultiScheduleCalendar: React.FC<MultiScheduleCalendarProps> = ({ courses }) => {
  const pathname = usePathname()
  const today = moment()

  const getInitialMonth = (): Moment => {
    for (const course of courses) {
      const hasToday = course?.courseDates?.lessonDates?.some((d) => d.isSame(today, "day"))
      if (hasToday) return today.clone().startOf("month")
    }
    return (
      courses[0]?.firstCourseDay?.clone().startOf("month") ??
      courses[0]?.courseDates?.lessonDates?.[0]?.clone().startOf("month") ??
      today.clone().startOf("month")
    )
  }

  const [currentMonth, setCurrentMonth] = useState<Moment>(getInitialMonth)

  useEffect(() => {
    setCurrentMonth(getInitialMonth())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const daysInMonth = currentMonth.daysInMonth()
  const startDay = currentMonth.startOf("month").day()
  const offset = (startDay + 6) % 7
  const monthLabel = currentMonth.format("MMMM YYYY")
  const days: JSX.Element[] = []

  for (let i = 0; i < offset; i++) {
    days.push(<div key={`empty-${i}`} />)
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const date = currentMonth.clone().date(d)
    const dateStr = date.format("YYYY-MM-DD")
    const isToday = date.isSame(today, "day")

    const matchingCourses = courses.filter((course) => course?.courseDates?.lessonDates?.some((lesson) => lesson.format("YYYY-MM-DD") === dateStr))

    const skippedCourses = courses.filter((course) => course?.courseDates?.skippedDates?.some((s) => s.format("YYYY-MM-DD") === dateStr))

    const isSkippedDate = skippedCourses.length > 0
    const background = isSkippedDate ? theme.colors["main-color-red"] : matchingCourses[0]?.color

    const popoverContent =
      matchingCourses.length === 0 && skippedCourses.length === 0 ? null : (
        <div>
          {[...matchingCourses, ...skippedCourses].map((course) => {
            const isSkipped = skippedCourses.includes(course)
            const time = course.firstCourseDay?.format("HH:mm") ?? course?.courseDates?.lessonDates?.[0]?.format("HH:mm") ?? "čas neznámý"
            return (
              <div key={course.id} style={{ marginBottom: 6 }}>
                <div>
                  {isSkipped ? "Není " : ""}
                  <strong>{course.name}</strong>
                </div>
                {!isSkipped && (
                  <div style={{ fontSize: 11 }}>
                    V {time} –{" "}
                    <a href={course.registrationUrl} target="_blank" rel="noopener noreferrer">
                      Přihlásit se
                    </a>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )

    const cell = (
      <DayCell key={dateStr} isToday={isToday} background={background}>
        {d}
      </DayCell>
    )

    days.push(
      popoverContent ? (
        <Popover key={dateStr} content={popoverContent} trigger="click" placement="top">
          {cell}
        </Popover>
      ) : (
        <React.Fragment key={dateStr}>{cell}</React.Fragment>
      )
    )
  }

  return (
    <div style={{ width: "100%" }}>
      <Navigation>
        <Button size="small" onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, "month"))}>
          ←
        </Button>
        <strong>{monthLabel}</strong>
        <Button size="small" onClick={() => setCurrentMonth(currentMonth.clone().add(1, "month"))}>
          →
        </Button>
      </Navigation>

      <CalendarWrapper>
        {daysOfWeek.map((d, i) => (
          <div key={i} className="week-days">
            {d}
          </div>
        ))}
      </CalendarWrapper>

      <CalendarWrapper>{days}</CalendarWrapper>
    </div>
  )
}

export default MultiScheduleCalendar
